/**
 * SSR/Hydration utilities for Stencil components
 *
 * These utilities help with parsing props during client-side hydration
 * when complex data structures are passed as JSON strings in HTML attributes.
 */

/**
 * Parse any JSON prop from HTML attribute string during client-side hydration.
 * Works with objects, arrays, and primitives.
 *
 * This function solves the SSR hydration issue where:
 * - Server passes complex props as objects/arrays
 * - HTML attributes can only contain strings
 * - Client receives JSON strings that need to be parsed back to objects
 *
 * Key features:
 * - Only runs on client-side (Build.isBrowser check enables tree-shaking for SSR bundle)
 * - Handles HTML-encoded quotes (&quot; → ")
 * - Type-safe: accepts both object/array and string types
 * - Fails gracefully: returns a fallback value or undefined if parsing fails
 *
 * @param value - Prop value (parsed on server, string on client)
 * @param fallback - Fallback value if parsing fails
 * @returns Parsed value or fallback
 *
 * @example
 * ```tsx
 * import { parseJsonProp } from '../../utils/helpers/hydration';
 *
 * @Component({ tag: 'my-component' })
 * export class MyComponent {
 *   @Prop({ mutable: true }) items?: Array<{ title: string }> | string;
 *   @Prop({ mutable: true }) config?: Record<string, any> | string;
 *
 *   componentWillLoad() {
 *     this.items = parseJsonProp(this.items, []);
 *     this.config = parseJsonProp(this.config);
 *   }
 * }
 * ```
 */
/**
 * Remove render-root duplicates left behind by a known Stencil hydration bug.
 *
 * Stencil bug (ionic-team/stencil#3413, related #2611/#2366, still open as of
 * Stencil 4.x): a scoped (`shadow: false`) component that renders its content via
 * `innerHTML` and is **slot-relocated** into another scoped component does not
 * reconcile against its server-rendered markup on the client — instead the client
 * re-renders the whole host subtree and APPENDS a second, identical render root.
 * The visible result is duplicated content on prerendered+hydrated pages.
 *
 * This keeps the **last** matching root (the freshly re-rendered node Stencil's
 * vdom now tracks, so future re-renders still patch a live node) and removes the
 * earlier, orphaned server-rendered copies.
 *
 * MUST be called from `componentDidLoad` and guarded with `Build.isBrowser`:
 * the fix only applies to the client hydration pass. On the server the component
 * already renders a single, correct root, so this must never run there.
 *
 * @param host - the component host element (`@Element()`)
 * @param selector - selector for the component's direct render root(s), e.g. `.dh-text`
 *
 * @example
 * ```tsx
 * @Element() host: HTMLElement;
 * componentDidLoad() {
 *   if (!Build.isBrowser || !this.rawHtml) return;
 *   dedupeHydratedRenderRoots(this.host, '.dh-text');
 * }
 * ```
 */
export function dedupeHydratedRenderRoots(host: HTMLElement, selector: string): void {
  const roots = host.querySelectorAll(`:scope > ${selector}`);
  if (roots.length <= 1) return;
  for (let i = 0; i < roots.length - 1; i++) roots[i].remove();
}

export function parseJsonProp<T>(value: T | string | undefined, fallback?: T): T | undefined {
  // If value is undefined/null, return fallback
  if (value === undefined || value === null) {
    return fallback;
  }

  // If value is not a string (already parsed object/array), return as is
  if (typeof value !== 'string') {
    return value as T;
  }

  // Parse JSON string (works on both server and client)
  try {
    // Decode HTML entities (&quot; → ")
    const decoded = value.replace(/&quot;/g, '"');
    const parsed = JSON.parse(decoded);
    return parsed as T;
  } catch {
    // Return fallback on parse error
    return fallback;
  }
}


/**
 * Normalize the `trigger-actions` value into a list of action ids.
 *
 * Three forms reach a component and all three are legitimate:
 *  - an **array**, from a property binding (a Stencil parent's JSX, Storybook, a framework);
 *  - a **JSON string**, which is what `json-to-html.ts` writes when the payload carries the prop;
 *  - a **comma-separated list**, which is what Stencil's `reflect: true` produces when a property
 *    binding is mirrored back to the attribute (`String(['a','b'])` is `"a,b"`, not JSON), and what
 *    a human or the template builder would naturally hand-author.
 *
 * Kept separate from `parseJsonProp` on purpose: that helper is used widely for genuine JSON props,
 * and teaching it to fall back to comma-splitting would change behaviour far outside actions.
 */
export function parseActionNames(value: unknown): string[] {
  if (value === undefined || value === null) return [];

  if (Array.isArray(value)) {
    return value.filter((v): v is string => typeof v === 'string' && v.trim() !== '').map((v) => v.trim());
  }

  if (typeof value !== 'string') return [];

  const decoded = value.replace(/&quot;/g, '"').trim();
  if (!decoded) return [];

  try {
    const parsed = JSON.parse(decoded);
    if (Array.isArray(parsed)) return parseActionNames(parsed);
    if (typeof parsed === 'string') return parseActionNames(parsed);
  } catch {
    // not JSON — fall through to the comma-separated form
  }

  return decoded
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);
}
